<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

    <!-- Sidebar -->
    <HrSidebar :isOpen="isSidebarOpen" />

    <!-- Main Content -->
    <main 
      class="flex-1 overflow-x-hidden transition-all duration-300"
      :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
    >

      <!-- Navbar -->
      <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
        <HrTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- Content -->
      <div class="p-6 mt-16">

        <!-- Top Bar -->
        <div class="flex justify-between items-center mb-8 p-4">

          <input
            v-model="search"
            type="text"
            placeholder="Search working hours rule..."
            class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:outline-none focus:ring focus:ring-secondary dark:bg-gray-700 dark:text-white"
          />

          <button
            @click="openCreate"
            class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
          >
            + Add Working Hours Rule
          </button>

        </div>

        <!-- Rules Grid -->
        <div
          v-if="filteredRules.length"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >

          <div
            v-for="rule in filteredRules"
            :key="rule.id"
            class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
          >

            <!-- Header -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center space-x-3">
                <div class="w-12 h-12 rounded-full bg-secondary text-white flex items-center justify-center font-bold text-xl">
                  {{ rule.category_name.charAt(0) }}
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800 dark:text-white text-lg">
                    {{ rule.category_name }}
                  </h3>
                  <span class="text-xs text-gray-500">Working Hours Rule</span>
                </div>
              </div>
            </div>

            <!-- Details -->
            <div class="space-y-3 mb-6">
              <div class="flex justify-between">
                <span class="text-gray-500">Start Time</span>
                <span class="font-medium text-[#035a76]">{{ rule.start_time }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">End Time</span>
                <span class="font-medium">{{ rule.end_time || 'N/A' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Standard Hours</span>
                <span class="font-medium">{{ rule.standard_hours }} hrs</span>
              </div>
            </div>

            <!-- Description -->
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2">
              {{ rule.description || 'No description provided' }}
            </p>

            <!-- Actions -->
            <div class="flex justify-between text-sm">
              <button
                @click="editRule(rule)"
                class="text-blue-600 hover:underline"
              >
                Edit
              </button>
              <button
                @click="deleteRule(rule.id)"
                class="text-red-500 hover:underline"
              >
                Delete
              </button>
            </div>

          </div>

        </div>

        <!-- Empty State -->
        <div
          v-else
          class="text-center text-gray-400 mt-20"
        >
          No working hours rules created yet
        </div>

        <!-- Modal -->
        <Transition name="modal">
          <div
            v-if="showModal"
            class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          >
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[460px] shadow-lg">

              <h3 class="text-lg font-semibold text-[#035a76] mb-6">
                {{ editing ? 'Edit Working Hours Rule' : 'Create New Working Hours Rule' }}
              </h3>

              <form @submit.prevent="saveRule" class="space-y-4">

                <input
                  v-model="form.category_name"
                  type="text"
                  placeholder="Category Name (e.g. Teacher, Support)"
                  required
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white"
                />

                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Start Time</label>
                    <input
                      v-model="form.start_time"
                      type="time"
                      required
                      class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">End Time</label>
                    <input
                      v-model="form.end_time"
                      type="time"
                      class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label class="block text-xs text-gray-500 mb-1">Standard Working Hours</label>
                  <input
                    v-model="form.standard_hours"
                    type="number"
                    step="0.5"
                    min="1"
                    placeholder="8.00"
                    class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <textarea
                  v-model="form.description"
                  placeholder="Description (optional)"
                  rows="3"
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white"
                ></textarea>

                <div class="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    @click="closeModal"
                    class="px-5 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-5 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
                  >
                    Save Rule
                  </button>
                </div>

              </form>
            </div>
          </div>
        </Transition>

      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Working Hours Rules")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)
const search = ref("")

const rules = ref([])

const form = ref({
  id: null,
  category_name: "",
  start_time: "",
  end_time: "",
  standard_hours: 8.00,
  description: ""
})

const fetchWorkingHours = async () => {
  try {
    const response = await api.get("WorkingHours.php")   // ← Your backend endpoint
    rules.value = response.data
  } catch (error) {
    console.error("Error fetching working hours rules:", error)
  }
}

const filteredRules = computed(() => {
  return rules.value.filter(r =>
    r.category_name.toLowerCase().includes(search.value.toLowerCase())
  )
})

const openCreate = () => {
  editing.value = false
  form.value = {
    id: null,
    category_name: "",
    start_time: "07:00",
    end_time: "15:00",
    standard_hours: 8.00,
    description: ""
  }
  showModal.value = true
}

const editRule = (rule) => {
  editing.value = true
  form.value = { ...rule }
  showModal.value = true
}

const saveRule = async () => {
  try {
    let response

    if (editing.value) {
      response = await api.put("WorkingHours.php", form.value)
      const index = rules.value.findIndex(r => r.id === form.value.id)
      if (index !== -1) rules.value[index] = { ...form.value }
    } else {
      response = await api.post("WorkingHours.php", form.value)
      rules.value.unshift({
        id: response.data.id,
        ...form.value
      })
    }

    closeModal()
  } catch (error) {
    console.error("Error saving working hours rule:", error)
    alert("Failed to save. Please try again.")
  }
}

const deleteRule = async (id) => {
  if (!confirm("Delete this working hours rule?")) return

  try {
    await api.delete("WorkingHours.php", { data: { id } })
    rules.value = rules.value.filter(r => r.id !== id)
  } catch (error) {
    console.error("Error deleting rule:", error)
    alert("Failed to delete rule")
  }
}

const closeModal = () => {
  showModal.value = false
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark")
  } else {
    document.documentElement.classList.remove("dark")
  }
}

onMounted(() => {
  fetchWorkingHours()
})
</script>

<style>
.modal-enter-active,
.modal-leave-active {
  transition: all .25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(.9);
}
</style>